import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import {
  NucleusSystemProperty,
  NucleusSystemPropertyFilter,
  NucleusSystemPropertyNotifierContract,
  NucleusSystemPropertyServiceContract,
  NucleusUpdateSystemPropertyPayload
} from '@nucleus/system-property-ui';
import { Page, PageResult, ServiceHandler, BaseComponent } from '@nucleus/ui-base';
import {
  SystemProperty,
  SystemPropertyControllerService,
  SystemPropertyFilter,
  UpdateSystemPropertyPayload
} from '@nucleus/system-property-ui';
import { AuditTraceComponent } from '../../common/audit-trace/audit-trace.component';
import { RequestHandler } from '../../services/request-handler';
import { FilterRequest } from '../../services/filter-request';
import { Filter } from '../../services/filter';

/**
 * NUC-274 / TASK-? (mirror of GOLD-92 / NUC-269): thin adapters wiring
 * tasksense's nucleus-system-property-service OpenAPI client +
 * PermissionChecker + BaseComponent's snackbar / showReport / audit-trail
 * dialog into the @nucleus/system-property-ui contracts.
 *
 * Skips the goldfish/bookwise local SystemPropertyService wrapper —
 * tasksense doesn't have one for this endpoint yet; the adapter calls
 * the generated OpenAPI client directly through the standard
 * RequestHandler so retries / loading / error toasts still work.
 */

class SystemPropertyFilterShim extends Filter {
  constructor(
    value: string = '',
    public application: string = '',
    public profile: string = '',
    public label: string = '',
  ) { super(value); }
  protected onReset(): void {
    this.application = '';
    this.profile = '';
    this.label = '';
  }
}

@Injectable({ providedIn: 'root' })
export class TasksenseSystemPropertyServiceAdapter implements NucleusSystemPropertyServiceContract {
  private api: SystemPropertyControllerService = inject(SystemPropertyControllerService);
  private requestHandler: RequestHandler = inject(RequestHandler);

  search(handler: ServiceHandler<NucleusSystemProperty[]>): void {
    this.requestHandler.handleRequest(
      this.api.findAll() as unknown as Observable<NucleusSystemProperty[]>,
      handler, false);
  }
  searchProfiles(handler: ServiceHandler<string[]>): void {
    this.requestHandler.handleRequest(this.api.findProfiles(), handler, false);
  }
  searchApplications(handler: ServiceHandler<string[]>): void {
    this.requestHandler.handleRequest(this.api.findApplications(), handler, false);
  }
  searchLabels(handler: ServiceHandler<string[]>): void {
    this.requestHandler.handleRequest(this.api.findLabels(), handler, false);
  }
  filter(handler: ServiceHandler<PageResult<NucleusSystemProperty>>, filter: NucleusSystemPropertyFilter, page?: Page): void {
    const request = this.toApiRequest(filter, page);
    this.requestHandler.handleRequest(
      this.api.filter(request as any) as unknown as Observable<PageResult<NucleusSystemProperty>>,
      handler, false);
  }
  update(payload: NucleusUpdateSystemPropertyPayload, handler: ServiceHandler<NucleusSystemProperty>): void {
    this.requestHandler.handleRequest(
      this.api.update(payload as unknown as UpdateSystemPropertyPayload) as unknown as Observable<NucleusSystemProperty>,
      handler, true);
  }
  getPDFReport(handler: ServiceHandler<Blob>, filter: NucleusSystemPropertyFilter, page: Page): void {
    const request = this.toApiRequest(filter, page);
    this.requestHandler.handleRequest(this.api.createPdfReport(request as any), handler, true);
  }
  getXLSReport(handler: ServiceHandler<Blob>, filter: NucleusSystemPropertyFilter, page: Page): void {
    const request = this.toApiRequest(filter, page);
    this.requestHandler.handleRequest(this.api.createExcelReport(request as any), handler, true);
  }
  getCSVReport(handler: ServiceHandler<Blob>, filter: NucleusSystemPropertyFilter, page: Page): void {
    const request = this.toApiRequest(filter, page);
    this.requestHandler.handleRequest(this.api.createCSVReport(request as any), handler, true);
  }

  private toApiRequest(filter: NucleusSystemPropertyFilter, page?: Page) {
    const shim = new SystemPropertyFilterShim(
      filter.value ?? '',
      filter.application ?? '',
      filter.profile ?? '',
      filter.label ?? '',
    );
    const request = new FilterRequest<SystemPropertyFilterShim>(shim);
    if (page) {
      request.page.pageNumber    = page.pageNumber ?? 0;
      request.page.pageSize      = page.pageSize ?? 10;
      request.page.sortField     = page.sortField ?? 'paramKey';
      request.page.sortDirection = page.sortDirection;
    }
    return request;
  }
}

/** Bridges the lib's notifier contract into BaseComponent's snackbar +
 *  showReport plumbing. Audit-trail opens tasksense's LOCAL
 *  AuditTraceComponent (same as access-request adapter pattern). */
@Injectable()
export class TasksenseSystemPropertyNotifierAdapter
  extends BaseComponent
  implements NucleusSystemPropertyNotifierContract {

  private matDialog: MatDialog = inject(MatDialog);

  success(message: string): void { super.showSuccess(message); }
  error(message: string):   void { super.showError(message); }
  accessDenied(): void { super.showAccessDeny(); }
  downloadReport(blob: Blob, filename: string, mimeType: string): void {
    super.showReport(blob, filename, mimeType);
  }
  openAuditTrail(entityName: string, entityId: string | number): void {
    this.matDialog.open(AuditTraceComponent, {
      width: '80vw',
      data: { entityName, entityId }
    });
  }
}
